import { useEffect, useMemo, useState } from 'react';

import { CONNECTIVITY_UI_OFFLINE_GRACE_MS } from '../../utils/connectivity-policy';
import { useBackendAvailable } from './use-backend-availability';
import { useSystemOnline } from './use-system-online';

export type ConnectivityOfflineReason = 'browser' | 'backend' | null;

export interface ConnectivityStatus {
  systemOnline: boolean;
  backendAvailable: boolean;
  offline: boolean;
  reason: ConnectivityOfflineReason;
}

export function useConnectivityStatus(): ConnectivityStatus {
  const systemOnline = useSystemOnline();
  const backendAvailable = useBackendAvailable();

  return useMemo(() => {
    const reason: ConnectivityOfflineReason = backendAvailable
      ? null
      : !systemOnline
        ? 'browser'
        : 'backend';

    return {
      systemOnline,
      backendAvailable,
      offline: reason !== null,
      reason,
    };
  }, [backendAvailable, systemOnline]);
}

export function useUiOfflineSignal(
  rawOffline: boolean,
  delayMs = CONNECTIVITY_UI_OFFLINE_GRACE_MS
): boolean {
  const [visibleOffline, setVisibleOffline] = useState(false);

  useEffect(() => {
    if (!rawOffline) {
      setVisibleOffline(false);
      return;
    }

    const timer = setTimeout(() => {
      setVisibleOffline(true);
    }, delayMs);

    return () => {
      clearTimeout(timer);
    };
  }, [delayMs, rawOffline]);

  return visibleOffline;
}
